/**
 * "Keep original files untouched" contract, shared by Compatibility and Match
 * Loudness: a keep-originals run only CREATES copies. Nothing references them
 * — not the DJ libraries (Rekordbox/Serato/Traktor/VirtualDJ), not the source
 * playlist, not the session list — until the user clicks "Use fixed files".
 *
 * This is that one toast. It is STICKY (no auto-dismiss): the pending updates
 * live nowhere else, so a timed toast that slipped by left the copies
 * un-committable. After the commit a timed Undo toast reverses everything
 * (DJ libraries + playlist + session list).
 */
import { repointPlaylistRows, invertSwaps } from './playlistRepoint';
import { track } from './analytics';

const FROM = { compat: 'fix_my_tracks', loudness: 'match_loudness' };

/**
 * @param {object} p
 * @param {object} p.toast            useToast() instance
 * @param {'compat'|'loudness'} p.source
 * @param {Array<{oldPath:string,newPath:string}>} p.pendingDjUpdates
 * @param {string} p.noun             e.g. 'fixed' | 'loudness-matched'
 * @param {object|null} p.playlistRef  {source, playlistId, rowIdsByPath} or null
 * @param {string} [p.playlistName]
 * @param {string} [p.accessToken]
 * @param {(byOld: Record<string,string>) => void} p.repointSession  swap session-list paths old→new
 */
export function offerFixedCopiesCommit({ toast, source, pendingDjUpdates, noun, playlistRef, playlistName, accessToken, repointSession }) {
    const n = pendingDjUpdates.length;
    if (!n) return;
    const plural = n > 1;
    const copies = `cop${plural ? 'ies' : 'y'}`;

    // Literal event names per call site — dynamic names evade check:analytics.
    const trackCommit = (props) => (source === 'compat'
        ? track('compat_copies_committed', props)
        : track('loudness_copies_committed', props));
    const applyDj = (updates) => window.electron.applyDjSync?.(updates, source)?.catch?.(() => {});
    const toSwaps = (updates) => updates.map((u) => ({
        oldPath: u.oldPath,
        newPath: u.newPath,
        rowId: playlistRef?.rowIdsByPath?.[u.oldPath] || null,
    }));

    const commit = () => {
        trackCommit({ count: n, playlist: !!playlistRef });
        applyDj(pendingDjUpdates);
        repointSession(Object.fromEntries(pendingDjUpdates.map((u) => [u.oldPath, u.newPath])));

        const finish = (appliedSwaps) => {
            if (playlistRef && appliedSwaps.length) {
                track('playlist_repointed', { from: FROM[source], count: appliedSwaps.length, undo: false });
            }
            if (playlistRef && appliedSwaps.length < n) {
                const missed = n - appliedSwaps.length;
                toast.warning(`Couldn't update “${playlistName}” — ${missed} track${missed > 1 ? 's' : ''} still point${missed > 1 ? '' : 's'} to the original file. Reload the playlist and run again.`, 12000);
            }
            const where = playlistRef && appliedSwaps.length ? ` and “${playlistName}”` : '';
            toast.showToast(
                `Your DJ libraries${where} now use the ${n} ${noun} ${copies}`,
                'success',
                12000,
                {
                    label: 'Undo',
                    onClick: () => {
                        trackCommit({ count: n, playlist: !!playlistRef, undo: true });
                        applyDj(invertSwaps(pendingDjUpdates));
                        repointSession(Object.fromEntries(pendingDjUpdates.map((u) => [u.newPath, u.oldPath])));
                        if (playlistRef && appliedSwaps.length) {
                            repointPlaylistRows({ ...playlistRef, accessToken, swaps: invertSwaps(appliedSwaps) })
                                .then(() => track('playlist_repointed', { from: FROM[source], count: appliedSwaps.length, undo: true }))
                                .catch(() => {});
                        }
                    },
                }
            );
        };

        if (playlistRef) {
            repointPlaylistRows({ ...playlistRef, accessToken, swaps: toSwaps(pendingDjUpdates) })
                .then(finish)
                .catch(() => finish([]));
        } else {
            finish([]);
        }
    };

    toast.showToast(
        `${n} ${noun} ${copies} created — your DJ libraries${playlistRef ? ` and “${playlistName}”` : ''} still use the originals`,
        'info',
        0, // sticky — the only place the pending commit lives
        { label: 'Use fixed files', onClick: commit }
    );
}
